// src/pages/sessions/components/LiveSession/PreJoinDeviceCheck.jsx
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { 
  VideoCameraIcon, 
  MicrophoneIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  ArrowPathIcon,
  ComputerDesktopIcon
} from '@heroicons/react/24/outline';

const STORAGE_KEYS = {
  AUDIO_DEVICE: 'simpleclass_audio_device',
  VIDEO_DEVICE: 'simpleclass_video_device',
  REMEMBER_CHOICE: 'simpleclass_remember_device_choice',
  MIC_VERIFIED: 'simpleclass_mic_verified'
};

// ─── Phone pre-join ───────────────────────────────────────────────────────────
// Simplified UI for phones: camera preview, mic level, join button.
// Device dropdowns are shown only when multiple devices are actually detected
// (covers Bluetooth headsets, front/back camera selection).
// Permission is requested on the Join button press, not on mount — required
// for iOS Safari which clears permissions on page reload and needs a user gesture.

const PhonePreJoin = ({ sessionData, onComplete, onCancel }) => {
  const { t } = useTranslation();
  const videoRef = useRef(null);
  const audioContextRef = useRef(null);
  const analyserRef = useRef(null);
  const audioStreamRef = useRef(null);
  const videoStreamRef = useRef(null);
  const animFrameRef = useRef(null);
  const mountedRef = useRef(true);

  const [audioDevices, setAudioDevices] = useState([]);
  const [videoDevices, setVideoDevices] = useState([]);
  const [selectedAudioId, setSelectedAudioId] = useState('');
  const [selectedVideoId, setSelectedVideoId] = useState('');
  const [audioLevel, setAudioLevel] = useState(0);
  const [cameraUnavailable, setCameraUnavailable] = useState(false);
  const [permissionError, setPermissionError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [permissionsGranted, setPermissionsGranted] = useState(false);

  const cleanup = useCallback(() => {
    if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current);
    if (audioContextRef.current) { try { audioContextRef.current.close(); } catch (_) {} }
    audioContextRef.current = null;
    analyserRef.current = null;
    if (audioStreamRef.current) audioStreamRef.current.getTracks().forEach(t => t.stop());
    audioStreamRef.current = null;
    if (videoStreamRef.current) videoStreamRef.current.getTracks().forEach(t => t.stop());
    videoStreamRef.current = null;
    if (videoRef.current) videoRef.current.srcObject = null;
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      cleanup();
    };
  }, [cleanup]);

  // Request permissions and start preview — called on the Join button press
  // so iOS Safari gets the required user gesture.
  const requestPermissionsAndPreview = useCallback(async () => {
    setIsLoading(true);
    setPermissionError(null);

    try {
      // Request permissions
      let stream;
      try {
        stream = await navigator.mediaDevices.getUserMedia({ audio: true, video: true });
      } catch (err) {
        if (err.name === 'AbortError') {
          // Chromium 145 bug: retry after delay
          console.warn('[PhonePreJoin] AbortError, retrying...');
          await new Promise(r => setTimeout(r, 1000));
          if (!mountedRef.current) return;
          try {
            stream = await navigator.mediaDevices.getUserMedia({ audio: true, video: true });
          } catch (retryErr) {
            console.warn('[PhonePreJoin] Retry failed, audio-only fallback');
            stream = await navigator.mediaDevices.getUserMedia({ audio: true, video: false });
            setCameraUnavailable(true);
          }
        } else if (err.name === 'NotReadableError') {
          stream = await navigator.mediaDevices.getUserMedia({ audio: true, video: false });
          setCameraUnavailable(true);
        } else {
          throw err;
        }
      }

      if (!mountedRef.current) { stream.getTracks().forEach(t => t.stop()); return; }
      stream.getTracks().forEach(t => t.stop()); // stop initial probe

      // Enumerate devices now that permission is granted
      const devices = await navigator.mediaDevices.enumerateDevices();
      if (!mountedRef.current) return;

      const audios = devices.filter(d => d.kind === 'audioinput' && !d.label.startsWith('Default - ') && !d.label.startsWith('Communications - '));
      const videos = devices.filter(d => d.kind === 'videoinput' && !d.label.startsWith('Default - ') && !d.label.startsWith('Communications - '));

      setAudioDevices(audios);
      setVideoDevices(videos);

      const audioId = audios[0]?.deviceId || '';
      const videoId = videos[0]?.deviceId || '';
      setSelectedAudioId(audioId);
      setSelectedVideoId(videoId);

      setPermissionsGranted(true);

      // Start camera preview
      if (videoId && !cameraUnavailable) {
        try {
          const vStream = await navigator.mediaDevices.getUserMedia({
            video: { deviceId: { exact: videoId }, facingMode: 'user' }
          });
          if (!mountedRef.current) { vStream.getTracks().forEach(t => t.stop()); return; }
          videoStreamRef.current = vStream;
          if (videoRef.current) videoRef.current.srcObject = vStream;
        } catch (vErr) {
          if (vErr.name === 'AbortError') {
            await new Promise(r => setTimeout(r, 2000));
            if (!mountedRef.current) return;
            try {
              const vStream2 = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'user' } });
              if (!mountedRef.current) { vStream2.getTracks().forEach(t => t.stop()); return; }
              videoStreamRef.current = vStream2;
              if (videoRef.current) videoRef.current.srcObject = vStream2;
            } catch (_) { setCameraUnavailable(true); }
          } else {
            setCameraUnavailable(true);
          }
        }
      }

      // Start mic monitoring
      if (audioId) {
        try {
          const aStream = await navigator.mediaDevices.getUserMedia({ audio: { deviceId: { exact: audioId } } });
          if (!mountedRef.current) { aStream.getTracks().forEach(t => t.stop()); return; }
          audioStreamRef.current = aStream;
          const ctx = new (window.AudioContext || window.webkitAudioContext)();
          audioContextRef.current = ctx;
          const source = ctx.createMediaStreamSource(aStream);
          const analyser = ctx.createAnalyser();
          analyser.fftSize = 256;
          analyser.smoothingTimeConstant = 0.9;
          source.connect(analyser);
          analyserRef.current = analyser;
          const data = new Uint8Array(analyser.frequencyBinCount);
          let smoothed = 0;
          const tick = () => {
            if (!mountedRef.current || !analyserRef.current) return;
            analyserRef.current.getByteFrequencyData(data);
            const avg = data.reduce((s, v) => s + v, 0) / data.length;
            const raw = Math.min(100, Math.round((avg / 50) * 100));
            smoothed = smoothed * 0.85 + raw * 0.15;
            setAudioLevel(Math.round(smoothed));
            if (smoothed > 10) { try { localStorage.setItem(STORAGE_KEYS.MIC_VERIFIED, 'true'); } catch (_) {} }
            animFrameRef.current = requestAnimationFrame(tick);
          };
          tick();
        } catch (_) {}
      }

    } catch (err) {
      if (!mountedRef.current) return;
      if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
        setPermissionError('denied');
      } else if (err.name === 'NotFoundError') {
        setPermissionError('not_found');
      } else {
        setPermissionError('unknown');
      }
    } finally {
      if (mountedRef.current) setIsLoading(false);
    }
  }, [cameraUnavailable]);

  // Handle camera device change
  useEffect(() => {
    if (!permissionsGranted || !selectedVideoId) return;
    const start = async () => {
      if (videoStreamRef.current) videoStreamRef.current.getTracks().forEach(t => t.stop());
      try {
        const s = await navigator.mediaDevices.getUserMedia({ video: { deviceId: { exact: selectedVideoId } } });
        if (!mountedRef.current) { s.getTracks().forEach(t => t.stop()); return; }
        videoStreamRef.current = s;
        setCameraUnavailable(false);
        if (videoRef.current) videoRef.current.srcObject = s;
      } catch (_) { setCameraUnavailable(true); }
    };
    start();
  }, [selectedVideoId, permissionsGranted]);

  const handleJoin = () => {
    cleanup();
    localStorage.setItem(STORAGE_KEYS.AUDIO_DEVICE, selectedAudioId);
    localStorage.setItem(STORAGE_KEYS.VIDEO_DEVICE, selectedVideoId);
    localStorage.setItem(STORAGE_KEYS.REMEMBER_CHOICE, 'true');
    onComplete(selectedAudioId, selectedVideoId, !cameraUnavailable);
  };

  const handleJoinPress = async () => {
    if (!permissionsGranted) {
      await requestPermissionsAndPreview();
      // After permissions granted, join immediately
      if (mountedRef.current && !permissionError) {
        cleanup();
        const audioId = selectedAudioId || audioDevices[0]?.deviceId || '';
        const videoId = selectedVideoId || videoDevices[0]?.deviceId || '';
        localStorage.setItem(STORAGE_KEYS.AUDIO_DEVICE, audioId);
        localStorage.setItem(STORAGE_KEYS.VIDEO_DEVICE, videoId);
        localStorage.setItem(STORAGE_KEYS.REMEMBER_CHOICE, 'true');
        onComplete(audioId, videoId, !cameraUnavailable);
      }
    } else {
      handleJoin();
    }
  };

  const showVideoDropdown = videoDevices.length > 1;
  const showAudioDropdown = audioDevices.length > 1;

  return (
    <div
      className="bg-gray-900 flex flex-col overflow-hidden"
      style={{
        position: 'fixed',
        inset: 0,
      }}
    >
      {/* Camera preview */}
      <div className="flex-1 relative bg-black overflow-hidden">
        <video
          ref={videoRef}
          autoPlay
          playsInline
          muted
          className="w-full h-full object-cover"
          style={{ transform: 'scaleX(-1)' }}
        />
        {!permissionsGranted && !isLoading && (
          <div className="absolute inset-0 flex flex-col items-center justify-center bg-gray-900/90">
            <VideoCameraIcon className="w-16 h-16 text-gray-600 mb-4" />
            <p className="text-gray-400 text-sm text-center px-8">
              {t('preJoinCheck.phone.tapToStart', 'Tap Join to enable camera & microphone')}
            </p>
          </div>
        )}
        {isLoading && (
          <div className="absolute inset-0 flex items-center justify-center bg-gray-900/80">
            <div className="w-10 h-10 border-4 border-cyan-500 border-t-transparent rounded-full animate-spin" />
          </div>
        )}
        {cameraUnavailable && permissionsGranted && (
          <div className="absolute inset-0 flex flex-col items-center justify-center bg-gray-800">
            <ExclamationTriangleIcon className="w-12 h-12 text-orange-400 mb-3" />
            <p className="text-orange-200 text-sm font-medium px-8 text-center">
              {t('preJoinCheck.cameraUnavailable.title')}
            </p>
            <p className="text-gray-400 text-xs px-8 text-center mt-1">
              {t('preJoinCheck.cameraUnavailable.description')}
            </p>
          </div>
        )}
      </div>

      {/* Bottom controls */}
      <div className="bg-gray-900 flex-shrink-0 px-4 pt-4 space-y-3" style={{ paddingBottom: 'max(8px, env(safe-area-inset-bottom))' }}>
        {/* Permission error */}
        {permissionError && (
          <div className="flex items-start gap-2 p-3 bg-red-900/40 border border-red-700/50 rounded-lg">
            <ExclamationTriangleIcon className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
            <p className="text-red-200 text-sm">
              {permissionError === 'denied'
                ? t('preJoinCheck.permissionDenied.message', 'Please allow camera and microphone access to join.')
                : t('preJoinCheck.permissionDenied.unknown', 'An error occurred. Please try again.')}
            </p>
          </div>
        )}

        {/* Device dropdowns — only when multiple devices detected */}
        {showVideoDropdown && (
          <select
            value={selectedVideoId}
            onChange={e => setSelectedVideoId(e.target.value)}
            className="w-full px-3 py-2.5 bg-gray-700 text-white rounded-lg border border-gray-600 text-sm"
          >
            {videoDevices.map((d, i) => (
              <option key={d.deviceId} value={d.deviceId}>{d.label || `Camera ${i + 1}`}</option>
            ))}
          </select>
        )}
        {showAudioDropdown && (
          <select
            value={selectedAudioId}
            onChange={e => setSelectedAudioId(e.target.value)}
            className="w-full px-3 py-2.5 bg-gray-700 text-white rounded-lg border border-gray-600 text-sm"
          >
            {audioDevices.map((d, i) => (
              <option key={d.deviceId} value={d.deviceId}>{d.label || `Microphone ${i + 1}`}</option>
            ))}
          </select>
        )}

        {/* Mic level */}
        {permissionsGranted && (
          <div className="flex items-center gap-3">
            <MicrophoneIcon className="w-5 h-5 text-gray-400 flex-shrink-0" />
            <div className="flex-1 h-2 bg-gray-700 rounded-full overflow-hidden">
              <div
                className={`h-full rounded-full transition-all duration-150 ${audioLevel > 10 ? 'bg-green-500' : 'bg-gray-500'}`}
                style={{ width: `${Math.min(100, Math.max(2, audioLevel * 2))}%` }}
              />
            </div>
            <span className="text-xs text-gray-500 w-14 text-right">
              {audioLevel > 10 ? t('preJoinCheck.audioDetected', 'Detected') : t('preJoinCheck.noAudio', 'Speak...')}
            </span>
          </div>
        )}

        {/* Buttons */}
        <div className="flex gap-3 pt-1">
          <button
            onClick={onCancel}
            className="flex-1 py-3 bg-gray-700 hover:bg-gray-600 text-white rounded-xl font-medium text-sm active:scale-95 transition-all"
          >
            {t('preJoinCheck.cancel', 'Cancel')}
          </button>
          <button
            onClick={handleJoinPress}
            disabled={isLoading}
            className="flex-2 px-6 py-3 bg-cyan-600 hover:bg-cyan-700 text-white rounded-xl font-semibold text-sm disabled:opacity-50 active:scale-95 transition-all"
            style={{ flex: 2 }}
          >
            {isLoading
              ? t('preJoinCheck.loading', 'Loading...')
              : t('preJoinCheck.joinSession', 'Join Session')}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Desktop / Tablet pre-join (full device check) ───────────────────────────

const DesktopPreJoin = ({ sessionData, onComplete, onCancel }) => {
  const { t } = useTranslation();
  
  const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
  
  const [audioDevices, setAudioDevices] = useState([]);
  const [videoDevices, setVideoDevices] = useState([]);
  const [selectedAudioDeviceId, setSelectedAudioDeviceId] = useState('');
  const [selectedVideoDeviceId, setSelectedVideoDeviceId] = useState('');
  
  const [audioLevel, setAudioLevel] = useState(0);
  const [hasPermission, setHasPermission] = useState(false);
  const [permissionError, setPermissionError] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [cameraUnavailable, setCameraUnavailable] = useState(false);
  
  const audioContextRef = useRef(null);
  const analyserRef = useRef(null);
  const audioStreamRef = useRef(null);
  const videoStreamRef = useRef(null);
  const animationFrameRef = useRef(null);
  const videoRef = useRef(null);
  const mountedRef = useRef(true);

  const cleanup = useCallback(() => {
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }
    if (audioContextRef.current) {
      try { audioContextRef.current.close(); } catch (e) {}
      audioContextRef.current = null;
      analyserRef.current = null;
    }
    if (audioStreamRef.current) {
      audioStreamRef.current.getTracks().forEach(track => track.stop());
      audioStreamRef.current = null;
    }
    if (videoStreamRef.current) {
      videoStreamRef.current.getTracks().forEach(track => track.stop());
      videoStreamRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    
    const initialize = async () => {
      setIsLoading(true);
      setPermissionError(null);
      
      try {
        let permissionStream;
        try {
          permissionStream = await navigator.mediaDevices.getUserMedia({ audio: true, video: true });
        } catch (mediaError) {
          if (mediaError.name === 'NotReadableError') {
            console.warn('[PreJoin] Camera not readable, falling back to audio-only');
            permissionStream = await navigator.mediaDevices.getUserMedia({ audio: true, video: false });
            setCameraUnavailable(true);
          } else if (mediaError.name === 'AbortError') {
            console.warn('[PreJoin] AbortError on initial camera request, retrying once...');
            await new Promise(resolve => setTimeout(resolve, 1000));
            if (!mountedRef.current) return;
            try {
              permissionStream = await navigator.mediaDevices.getUserMedia({ audio: true, video: true });
            } catch (retryError) {
              console.warn('[PreJoin] Retry also failed, falling back to audio-only:', retryError.message);
              permissionStream = await navigator.mediaDevices.getUserMedia({ audio: true, video: false });
              setCameraUnavailable(true);
            }
          } else {
            throw mediaError;
          }
        }
        
        if (!mountedRef.current) {
          permissionStream.getTracks().forEach(track => track.stop());
          return;
        }
        
        permissionStream.getTracks().forEach(track => track.stop());
        setHasPermission(true);
        
        const devices = await navigator.mediaDevices.enumerateDevices();
        if (!mountedRef.current) return;
        
        const audioInputs = devices.filter(d => d.kind === 'audioinput');
        const videoInputs = devices.filter(d => d.kind === 'videoinput');
        
        const uniqueAudioInputs = audioInputs.filter(d => 
          !d.label.startsWith('Default - ') && !d.label.startsWith('Communications - ')
        );
        const uniqueVideoInputs = videoInputs.filter(d => 
          !d.label.startsWith('Default - ') && !d.label.startsWith('Communications - ')
        );
        
        setAudioDevices(uniqueAudioInputs);
        setVideoDevices(uniqueVideoInputs);
        
        const savedAudioDevice = localStorage.getItem(STORAGE_KEYS.AUDIO_DEVICE);
        const savedVideoDevice = localStorage.getItem(STORAGE_KEYS.VIDEO_DEVICE);
        
        const audioId = (savedAudioDevice && uniqueAudioInputs.some(d => d.deviceId === savedAudioDevice))
          ? savedAudioDevice : (uniqueAudioInputs[0]?.deviceId || '');
        const videoId = (savedVideoDevice && uniqueVideoInputs.some(d => d.deviceId === savedVideoDevice))
          ? savedVideoDevice : (uniqueVideoInputs[0]?.deviceId || '');
        
        setSelectedAudioDeviceId(audioId);
        setSelectedVideoDeviceId(videoId);
        
      } catch (error) {
        console.error('Permission error:', error);
        if (!mountedRef.current) return;
        if (error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError') {
          setPermissionError('denied');
        } else if (error.name === 'NotFoundError') {
          setPermissionError('not_found');
        } else {
          setPermissionError('unknown');
        }
      } finally {
        if (mountedRef.current) setIsLoading(false);
      }
    };
    
    initialize();
    
    const handleDeviceChange = async () => {
      try {
        const devices = await navigator.mediaDevices.enumerateDevices();
        if (!mountedRef.current) return;
        const audioInputs = devices.filter(d => d.kind === 'audioinput')
          .filter(d => !d.label.startsWith('Default - ') && !d.label.startsWith('Communications - '));
        const videoInputs = devices.filter(d => d.kind === 'videoinput')
          .filter(d => !d.label.startsWith('Default - ') && !d.label.startsWith('Communications - '));
        setAudioDevices(audioInputs);
        setVideoDevices(videoInputs);
      } catch (e) {}
    };
    
    navigator.mediaDevices.addEventListener('devicechange', handleDeviceChange);
    
    return () => {
      mountedRef.current = false;
      cleanup();
      navigator.mediaDevices.removeEventListener('devicechange', handleDeviceChange);
    };
  }, [cleanup]);

  useEffect(() => {
    if (!hasPermission || !selectedAudioDeviceId) return;
    
    const startAudioMonitoring = async () => {
      if (audioStreamRef.current) audioStreamRef.current.getTracks().forEach(t => t.stop());
      if (animationFrameRef.current) cancelAnimationFrame(animationFrameRef.current);
      if (audioContextRef.current) { try { audioContextRef.current.close(); } catch (e) {} }
      
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: { deviceId: { exact: selectedAudioDeviceId } }
        });
        if (!mountedRef.current) { stream.getTracks().forEach(t => t.stop()); return; }
        audioStreamRef.current = stream;
        const audioContext = new (window.AudioContext || window.webkitAudioContext)();
        audioContextRef.current = audioContext;
        const source = audioContext.createMediaStreamSource(stream);
        const analyser = audioContext.createAnalyser();
        analyser.fftSize = 256;
        analyser.smoothingTimeConstant = 0.95;
        source.connect(analyser);
        analyserRef.current = analyser;
        const dataArray = new Uint8Array(analyser.frequencyBinCount);
        let smoothedLevel = 0;
        const updateLevel = () => {
          if (!mountedRef.current || !analyserRef.current) return;
          analyserRef.current.getByteFrequencyData(dataArray);
          let sum = 0;
          for (let i = 0; i < dataArray.length; i++) sum += dataArray[i];
          const average = sum / dataArray.length;
          const rawLevel = Math.min(100, Math.round((average / 50) * 100));
          smoothedLevel = smoothedLevel * 0.85 + rawLevel * 0.15;
          const level = Math.round(smoothedLevel);
          setAudioLevel(level);
          if (level > 10) { try { localStorage.setItem(STORAGE_KEYS.MIC_VERIFIED, 'true'); } catch {} }
          animationFrameRef.current = requestAnimationFrame(updateLevel);
        };
        updateLevel();
      } catch (error) {
        console.error('Error starting audio monitoring:', error);
      }
    };
    
    startAudioMonitoring();
    return () => { if (animationFrameRef.current) cancelAnimationFrame(animationFrameRef.current); };
  }, [hasPermission, selectedAudioDeviceId]);

  useEffect(() => {
    if (!hasPermission || !selectedVideoDeviceId) return;
    
    const startVideoPreview = async () => {
      if (videoStreamRef.current) videoStreamRef.current.getTracks().forEach(t => t.stop());
      
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { deviceId: { exact: selectedVideoDeviceId } }
        });
        if (!mountedRef.current) { stream.getTracks().forEach(t => t.stop()); return; }
        videoStreamRef.current = stream;
        setCameraUnavailable(false);
        if (videoRef.current) videoRef.current.srcObject = stream;
      } catch (error) {
        console.error('Error starting video preview:', error);
        if (error.name === 'NotReadableError') {
          setCameraUnavailable(true);
        } else if (error.name === 'AbortError') {
          console.warn('[PreJoin] AbortError on video preview, retrying...');
          const constraints = { video: { deviceId: { exact: selectedVideoDeviceId } } };
          let recovered = false;
          for (const delay of [2000, 3000]) {
            await new Promise(resolve => setTimeout(resolve, delay));
            if (!mountedRef.current) return;
            try {
              const retryStream = await navigator.mediaDevices.getUserMedia(constraints);
              if (!mountedRef.current) { retryStream.getTracks().forEach(t => t.stop()); return; }
              videoStreamRef.current = retryStream;
              setCameraUnavailable(false);
              if (videoRef.current) videoRef.current.srcObject = retryStream;
              recovered = true;
              break;
            } catch (retryError) {
              console.warn('[PreJoin] Retry failed:', retryError.name);
            }
          }
          if (!recovered) setCameraUnavailable(true);
        }
      }
    };
    
    startVideoPreview();
  }, [hasPermission, selectedVideoDeviceId]);

  const handleJoin = () => {
    localStorage.setItem(STORAGE_KEYS.AUDIO_DEVICE, selectedAudioDeviceId);
    localStorage.setItem(STORAGE_KEYS.VIDEO_DEVICE, selectedVideoDeviceId);
    localStorage.setItem(STORAGE_KEYS.REMEMBER_CHOICE, 'true');
    cleanup();
    onComplete(selectedAudioDeviceId, selectedVideoDeviceId, !cameraUnavailable);
  };

  const handleCancel = () => { cleanup(); onCancel(); };

  const handleRetry = () => {
    mountedRef.current = false;
    setTimeout(() => { mountedRef.current = true; window.location.reload(); }, 100);
  };

  if (isLoading) {
    return (
      <div className="fixed inset-0 bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-cyan-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-white text-lg">{t('preJoinCheck.loading', 'Requesting camera and microphone access...')}</p>
        </div>
      </div>
    );
  }

  if (permissionError) {
    return (
      <div className="fixed inset-0 bg-gray-900 flex items-center justify-center p-4 overflow-y-auto">
        <div className="bg-gray-800 rounded-xl max-w-md w-full p-4 sm:p-6 text-center my-auto">
          <ExclamationTriangleIcon className="w-12 h-12 sm:w-16 sm:h-16 text-yellow-500 mx-auto mb-3 sm:mb-4" />
          <h2 className="text-lg sm:text-xl font-semibold text-white mb-2">
            {t('preJoinCheck.permissionDenied.title', 'Permission Required')}
          </h2>
          <p className="text-gray-300 mb-3 sm:mb-4 text-sm sm:text-base">
            {permissionError === 'denied' 
              ? t('preJoinCheck.permissionDenied.message', 'Please allow camera and microphone access to join the session.')
              : permissionError === 'not_found'
              ? t('preJoinCheck.permissionDenied.notFound', 'No camera or microphone found on this device.')
              : t('preJoinCheck.permissionDenied.unknown', 'An error occurred while accessing your devices.')
            }
          </p>
          <p className="text-gray-400 text-xs sm:text-sm mb-4 sm:mb-6">
            {t('preJoinCheck.permissionDenied.instructions', 'Click the camera/microphone icon in your browser\'s address bar and allow access, then try again.')}
          </p>
          <div className="flex gap-3">
            <button onClick={handleCancel} className="flex-1 px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors text-sm sm:text-base">
              {t('preJoinCheck.cancel', 'Cancel')}
            </button>
            <button onClick={handleRetry} className="flex-1 px-4 py-2 bg-cyan-600 hover:bg-cyan-700 text-white rounded-lg transition-colors flex items-center justify-center gap-2 text-sm sm:text-base">
              <ArrowPathIcon className="w-4 h-4" />
              {t('preJoinCheck.tryAgain', 'Try Again')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-gray-900 flex items-center justify-center p-2 sm:p-4 overflow-y-auto">
      <div className="bg-gray-800 rounded-xl w-full max-w-[320px] sm:max-w-md lg:max-w-lg shadow-2xl my-auto">
        <div className="px-3 sm:px-4 lg:px-6 py-2 sm:py-3 lg:py-4 border-b border-gray-700">
          <div className="flex items-center gap-2 sm:gap-3">
            <div className="p-1 sm:p-1.5 lg:p-2 bg-cyan-600 rounded lg:rounded-lg">
              <VideoCameraIcon className="w-4 h-4 sm:w-5 sm:h-5 lg:w-6 lg:h-6 text-white" />
            </div>
            <h2 className="text-sm sm:text-base lg:text-xl font-semibold text-white">
              {t('preJoinCheck.title', 'Check Your Camera & Microphone')}
            </h2>
          </div>
        </div>

        <div className="p-3 sm:p-4 lg:p-6 space-y-2 sm:space-y-3 lg:space-y-4">
          <div className="aspect-video max-h-24 sm:max-h-36 lg:max-h-52 bg-gray-900 rounded-lg overflow-hidden relative">
            <video ref={videoRef} autoPlay playsInline muted className="w-full h-full object-cover" />
            {!selectedVideoDeviceId && (
              <div className="absolute inset-0 flex items-center justify-center">
                <p className="text-gray-500 text-xs sm:text-sm">{t('preJoinCheck.noCamera', 'No camera selected')}</p>
              </div>
            )}
          </div>
          
          <select value={selectedVideoDeviceId} onChange={e => setSelectedVideoDeviceId(e.target.value)} className="w-full px-2 sm:px-3 py-1.5 sm:py-2 lg:py-2.5 bg-gray-700 text-white rounded-lg border border-gray-600 focus:border-cyan-500 focus:outline-none text-xs sm:text-sm lg:text-base">
            {videoDevices.length === 0 ? (
              <option value="">{t('preJoinCheck.noDevices.camera', 'No camera found')}</option>
            ) : (
              videoDevices.map((device, index) => (
                <option key={device.deviceId} value={device.deviceId}>{device.label || `Camera ${index + 1}`}</option>
              ))
            )}
          </select>

          {cameraUnavailable && (
            <div className="flex items-start gap-2 p-2 sm:p-3 bg-orange-900/30 border border-orange-600/60 rounded-lg">
              <ExclamationTriangleIcon className="w-4 h-4 sm:w-5 sm:h-5 text-orange-400 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-orange-200 text-xs sm:text-sm font-medium">{t('preJoinCheck.cameraUnavailable.title')}</p>
                <p className="text-gray-400 text-xs mt-0.5">{t('preJoinCheck.cameraUnavailable.description')}</p>
              </div>
            </div>
          )}

          <select value={selectedAudioDeviceId} onChange={e => setSelectedAudioDeviceId(e.target.value)} className="w-full px-2 sm:px-3 py-1.5 sm:py-2 lg:py-2.5 bg-gray-700 text-white rounded-lg border border-gray-600 focus:border-cyan-500 focus:outline-none text-xs sm:text-sm lg:text-base">
            {audioDevices.length === 0 ? (
              <option value="">{t('preJoinCheck.noDevices.microphone', 'No microphone found')}</option>
            ) : (
              audioDevices.map((device, index) => (
                <option key={device.deviceId} value={device.deviceId}>{device.label || `Microphone ${index + 1}`}</option>
              ))
            )}
          </select>

          <div>
            <div className="flex items-center justify-between text-xs sm:text-sm mb-1">
              <span className="text-gray-400">{t('preJoinCheck.volumeLevel', 'Volume Level')}</span>
              <span className={`flex items-center gap-1 ${audioLevel > 10 ? 'text-green-400' : 'text-gray-500'}`}>
                {audioLevel > 10 ? (
                  <><CheckCircleIcon className="w-3 h-3 sm:w-4 sm:h-4" />{t('preJoinCheck.audioDetected', 'Audio detected')}</>
                ) : t('preJoinCheck.noAudio', 'Speak to test...')}
              </span>
            </div>
            <div className="h-2 sm:h-2.5 lg:h-3 bg-gray-700 rounded-full overflow-hidden">
              <div
                className={`h-full transition-all duration-200 ease-out ${audioLevel > 10 ? 'bg-green-500' : 'bg-gray-500'}`}
                style={{ width: `${Math.min(100, Math.max(2, audioLevel * 2))}%` }}
              />
            </div>
          </div>

          {isSafari && (
            <div className="flex items-start gap-2 p-2 sm:p-3 bg-yellow-900/20 border border-yellow-700/50 rounded-lg">
              <ComputerDesktopIcon className="w-4 h-4 sm:w-5 sm:h-5 text-yellow-500 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-yellow-200 text-xs sm:text-sm font-medium">{t('preJoinCheck.safari.title', 'Safari limitation')}</p>
                <p className="text-gray-400 text-xs mt-0.5">{t('preJoinCheck.safari.description', 'Can only share entire screen. Use Chrome/Firefox for window sharing.')}</p>
              </div>
            </div>
          )}
        </div>

        <div className="px-3 sm:px-4 lg:px-6 py-2 sm:py-3 lg:py-4 border-t border-gray-700 flex gap-2 sm:gap-3">
          <button onClick={handleCancel} className="flex-1 px-3 sm:px-4 py-1.5 sm:py-2 lg:py-2.5 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors text-xs sm:text-sm lg:text-base font-medium">
            {t('preJoinCheck.cancel', 'Cancel')}
          </button>
          <button onClick={handleJoin} disabled={!selectedAudioDeviceId && !selectedVideoDeviceId} className="flex-1 px-3 sm:px-4 py-1.5 sm:py-2 lg:py-2.5 bg-cyan-600 hover:bg-cyan-700 text-white rounded-lg transition-colors text-xs sm:text-sm lg:text-base font-medium disabled:opacity-50 disabled:cursor-not-allowed">
            {t('preJoinCheck.joinSession', 'Join Session')}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Router ───────────────────────────────────────────────────────────────────

const PreJoinDeviceCheck = ({ sessionData, deviceType = 'desktop', onComplete, onCancel }) => {
  if (deviceType === 'phone') {
    return <PhonePreJoin sessionData={sessionData} onComplete={onComplete} onCancel={onCancel} />;
  }
  return <DesktopPreJoin sessionData={sessionData} onComplete={onComplete} onCancel={onCancel} />;
};

export { STORAGE_KEYS };
export default PreJoinDeviceCheck;
